import { useEffect } from "react";
import { SAUNA_ORIGIN } from "@/data/sauna-seo";
import { BLOG_PATH, type BlogPost } from "@/data/blog";

const ORG = { "@type": "Organization", "@id": `${SAUNA_ORIGIN}/#organization`, name: "VertexAir Climate Systems", alternateName: "Vertex Climate", url: SAUNA_ORIGIN };

export interface BlogSeoInput { title: string; description: string; path: string; image: string; noindex?: boolean; post?: BlogPost }

export function blogSchemas(input: BlogSeoInput) {
  const url = SAUNA_ORIGIN + input.path;
  const crumbs = [{ name: "Home", item: `${SAUNA_ORIGIN}/` }, { name: "Blog", item: SAUNA_ORIGIN + BLOG_PATH }];
  if (input.post) crumbs.push({ name: input.post.title, item: url });
  return [
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, ...c })) },
    input.post
      ? { "@context": "https://schema.org", "@type": "BlogPosting", headline: input.post.title, description: input.description, mainEntityOfPage: url, image: `${SAUNA_ORIGIN}/images/${input.image}`, articleSection: input.post.category, wordCount: input.post.wordCount, keywords: [input.post.primaryKeyword, ...input.post.supportingKeywords].join(", "), author: ORG, publisher: ORG }
      : { "@context": "https://schema.org", "@type": "Blog", name: input.title, url, publisher: ORG },
  ];
}

export function useBlogSeo(input: BlogSeoInput) {
  const key = `${input.path}|${input.noindex ? 1 : 0}`;
  useEffect(() => {
    const prevTitle = document.title;
    document.title = input.title;
    const removed = [...document.head.querySelectorAll('meta[name="description"],meta[name="robots"],meta[name^="twitter:"],meta[property^="og:"],link[rel="canonical"],script[type="application/ld+json"]')];
    removed.forEach(n => n.remove());
    const added: Element[] = [];
    const add = (tag: string, attrs: Record<string, string>, text?: string) => {
      const n = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => n.setAttribute(k, v));
      if (text) n.textContent = text;
      document.head.appendChild(n); added.push(n);
    };
    add("meta", { name: "description", content: input.description });
    if (input.noindex) {
      add("meta", { name: "robots", content: "noindex, follow" });
    } else {
      const url = SAUNA_ORIGIN + input.path;
      const img = `${SAUNA_ORIGIN}/images/${input.image}`;
      add("link", { rel: "canonical", href: url });
      add("meta", { name: "robots", content: "index, follow, max-image-preview:large" });
      for (const [name, content] of Object.entries({ "twitter:card": "summary_large_image", "twitter:title": input.title, "twitter:description": input.description, "twitter:image": img })) add("meta", { name, content });
      for (const [property, content] of Object.entries({ "og:title": input.title, "og:description": input.description, "og:url": url, "og:type": input.post ? "article" : "website", "og:image": img, "og:locale": "en_GB" })) add("meta", { property, content });
      blogSchemas(input).forEach(s => add("script", { type: "application/ld+json" }, JSON.stringify(s)));
    }
    return () => { added.forEach(n => n.remove()); removed.forEach(n => document.head.appendChild(n)); document.title = prevTitle; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
